// Site-aware Link — auto-strips the site prefix when running on a matched production domain
// so that `<Link to="/games/cricketer/X">` becomes `/cricketer/X` on gamesnfans.tv
// but stays `/games/cricketer/X` on the preview / all-mode domain.
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import siteConfig from '@/config/siteConfig';
import { useLang, localizePath } from '@/i18n/dgtgLang';

const SITE_PREFIX = {
  games: '/games',
  dgtg: '/dgtg',
  devmob: '/devmob',
  vikas: '/vikas',
  auto: '/auto',
  coupon: '/coupons',
  finance: '/finance',
  travel: '/travel',
  tech: '/tech',
};

// `/tech/blog?topic=ai` → `/blog?topic=ai` for site 'tech': the path as that site's own domain serves it.
// `/dgtg#world-data` and `/dgtg?x=1` are the site's own homepage too.
export function stripSitePrefix(site, to) {
  const prefix = SITE_PREFIX[site];
  if (prefix && (to === prefix || to.startsWith(prefix + '/') || to.startsWith(prefix + '#') || to.startsWith(prefix + '?'))) {
    const stripped = to.slice(prefix.length);
    return !stripped ? '/' : /^[#?]/.test(stripped) ? `/${stripped}` : stripped; // `/games` → `/`, `/dgtg#x` → `/#x`
  }
  return to;
}

// Returns the path to use in the URL, stripped of the site prefix if we're on that site's production domain.
export function siteLink(to) {
  if (!to || typeof to !== 'string') return to;
  if (!to.startsWith('/')) return to; // external or special protocol

  const sid = siteConfig.siteId;
  // Only strip when we're on the matching production domain (NOT in 'all' / preview mode)
  if (sid && sid !== 'all') return stripSitePrefix(sid, to);
  return to;
}

// Returns the canonical absolute URL for the current production domain
export function canonicalUrl(absoluteUrl) {
  if (!absoluteUrl || typeof absoluteUrl !== 'string') return absoluteUrl;
  const sid = siteConfig.siteId;
  if (sid && sid !== 'all') {
    const prefix = SITE_PREFIX[sid];
    if (prefix) {
      // Strip `/games` from anywhere in the path component
      try {
        const u = new URL(absoluteUrl);
        if (u.pathname === prefix || u.pathname.startsWith(prefix + '/')) {
          u.pathname = u.pathname.slice(prefix.length) || '/';
        }
        return u.toString().replace(/\/$/, '');
      } catch {
        return absoluteUrl;
      }
    }
  }
  return absoluteUrl;
}

const SiteLink = React.forwardRef(function SiteLink({ to, ...rest }, ref) {
  // Inside a translated DGTG page, links to pages that exist in that language stay in it.
  const { lang } = useLang();
  return <RouterLink ref={ref} to={siteLink(localizePath(to, lang))} {...rest} />;
});

export default SiteLink;
